<!-- src/lib/components/CardItem.svelte -->
<script lang="ts">
  import type { Card, Label } from '../types.ts';
  import { api } from '../api.ts';
  import { agentStore } from '../stores/agents.svelte.ts';
  import { pmStore } from '../stores/pm.svelte.ts';
  import { cardHeat, heatBorderColor, heatGlow } from '../stores/vitals.svelte.ts';
  import { onDestroy, onMount } from 'svelte';
  import StatusIcon from './StatusIcon.svelte';
  import PrioritySignal from './PrioritySignal.svelte';
  import LabelChip from './LabelChip.svelte';
  import Avatar from './Avatar.svelte';
  import { timeAgo, dueUrgency, formatDueDate as fmtDue, DUE_URGENCY_COLORS } from '../format-time.ts';

  interface Props {
    card: Card;
    onOpen: (id: string) => void;
    /** Resolved column name for the status icon (Todo / In Progress / Done …). */
    columnName?: string;
    selectedCardIds?: Set<string>;
    onSelect?: (cardId: string, selected: boolean) => void;
    /** Optional repo badge chip shown after the ID chip (for cross-repo views). */
    repoBadge?: string;
  }
  let { card, onOpen, columnName = 'todo', selectedCardIds, onSelect, repoBadge }: Props = $props();

  let starting  = $state(false);
  let isNew     = $state(true);
  let heatLevel = $state(0);
  let nowMs = $state(Date.now());
  let tickInterval: ReturnType<typeof setInterval> | null = null;

  // ── Hover preview (refinement #18) ─────────────────────────────
  // After 400ms of hovering, a tooltip shows title + first ~3 lines
  // of description + label names. Helps scan the backlog without
  // committing to a drawer click.
  let showPreview   = $state(false);
  let previewTimer: ReturnType<typeof setTimeout> | null = null;

  function onHoverStart() {
    previewTimer = setTimeout(() => { showPreview = true; }, 400);
  }
  function onHoverEnd() {
    if (previewTimer) { clearTimeout(previewTimer); previewTimer = null; }
    showPreview = false;
  }

  const previewDesc = $derived((() => {
    if (!card.description) return '';
    const lines = card.description.split('\n').slice(0, 3);
    const text = lines.join('\n');
    return text.length > 160 ? text.slice(0, 157) + '…' : text;
  })());
  let rippling  = $state(false);
  let decaying  = $state(false);

  // Plain mutable refs — not $state, so writing them inside $effect
  // doesn't re-trigger the effect.
  let _prevUpdated: string | null       = null;
  let _prevAgentId: string | null | undefined = undefined;

  // Fade "new" glow after 3s
  $effect(() => {
    const t = setTimeout(() => { isNew = false; }, 3000);
    return () => clearTimeout(t);
  });

  // Heat, ripple, decay — single effect tracks all card prop changes
  $effect(() => {
    const updated = card.updated_at;
    const agentId = card.agent_id;

    // Recompute heat immediately on any card change
    heatLevel = cardHeat(updated);

    // Signal ripple when updated_at changes (skip first run)
    if (_prevUpdated !== null && updated !== _prevUpdated) {
      rippling = true;
      setTimeout(() => { rippling = false; }, 600);
    }
    _prevUpdated = updated;

    // Particle decay when agent completes (non-null → null)
    if (_prevAgentId !== undefined && _prevAgentId !== null && agentId === null) {
      decaying = true;
      setTimeout(() => { decaying = false; }, 1200);
    }
    _prevAgentId = agentId;

    // Keep heat cooling over time
    const iv = setInterval(() => { heatLevel = cardHeat(card.updated_at); }, 60_000);
    return () => clearInterval(iv);
  });

  // Fetch labels on mount if not cached
  onMount(async () => {
    if (!pmStore.cardLabels[card.id]) {
      try {
        const labels = await api<Label[]>(`/api/v1/cards/${card.id}/labels`);
        pmStore.cardLabels[card.id] = labels;
      } catch { /* non-critical */ }
    }
  });

  let labels = $derived(pmStore.cardLabels[card.id] ?? []);
  const queuedAgent = $derived(
    card.agent_id ? agentStore.queued.find((task) => task.agent_id === card.agent_id) ?? null : null
  );
  const runningAgent = $derived(
    card.agent_id ? agentStore.running.find((run) => run.agent_id === card.agent_id) ?? null : null
  );

  const priorityColor: Record<string, string> = {
    urgent: 'oklch(0.62 0.26 25)',
    high:   'oklch(0.78 0.16 85)',
    medium: 'oklch(0.58 0.22 265)',
    low:    'rgba(255,255,255,0.25)',
    none:   'rgba(255,255,255,0.1)',
  };

  const urgency = $derived(dueUrgency(card.due_date));
  const urgencyColor = $derived(DUE_URGENCY_COLORS[urgency]);

  $effect(() => {
    if (runningAgent) {
      if (!tickInterval) {
        tickInterval = setInterval(() => { nowMs = Date.now(); }, 1000);
      }
    } else if (tickInterval) {
      clearInterval(tickInterval);
      tickInterval = null;
    }
  });

  onDestroy(() => {
    if (tickInterval) clearInterval(tickInterval);
  });

  function updateCardAgent(agentId: string) {
    for (const board of Object.values(pmStore.boardDetails)) {
      for (const column of board.columns) {
        const storedCard = column.cards.find((item) => item.id === card.id);
        if (storedCard) storedCard.agent_id = agentId;
      }
    }
  }

  async function startWork(e: MouseEvent) {
    e.stopPropagation();
    starting = true;
    try {
      const result = await api<{ agent_id: string }>(`/api/v1/cards/${card.id}/start`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      updateCardAgent(result.agent_id);
    } finally {
      starting = false;
    }
  }

  // Card short ID: last 4 chars of UUID
  // Prefer the stable human_id (e.g. "LOC-17"). Fall back to a UUID
  // suffix for cards created before migration 017 or any that somehow
  // lack a human_id.
  let cardShortId = $derived(card.human_id ?? `#${card.id.slice(-4)}`);

  // Visible labels: up to 2, then "+N" overflow
  let visibleLabels = $derived(labels.slice(0, 2));
  let overflowCount = $derived(labels.length > 2 ? labels.length - 2 : 0);

  function timeInStatus(updatedAt: string): string {
    const diff = Date.now() - new Date(updatedAt).getTime();
    const hours = diff / 3600000;
    if (hours < 1)   return '<1h';
    if (hours < 24)  return `${Math.floor(hours)}h`;
    const days = Math.floor(hours / 24);
    if (days < 7)    return `${days}d`;
    const weeks = Math.floor(days / 7);
    if (weeks < 4)   return `${weeks}w`;
    return `${Math.floor(days / 30)}mo`;
  }

  function timeInStatusColor(updatedAt: string): string {
    const diff = Date.now() - new Date(updatedAt).getTime();
    const hours = diff / 3600000;
    if (hours < 24)  return 'rgba(255,255,255,0.15)';
    const days = hours / 24;
    if (days < 3)    return 'rgba(255,180,50,0.2)';
    if (days < 7)    return 'rgba(255,120,30,0.3)';
    return 'rgba(255,60,60,0.35)';
  }

  function agentElapsed(startedAt: string): string {
    const ms = nowMs - new Date(startedAt).getTime();
    if (ms < 0) return '0s';
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ${seconds % 60}s`;
  }

  function formatEstimate(estimate: number | null | undefined): string {
    if (estimate == null || estimate <= 0) return '';
    const value = Number.isInteger(estimate) ? String(estimate) : estimate.toFixed(1).replace(/\.0$/, '');
    return `${value} pt`;
  }

  function formatWorktree(path: string | null | undefined): string {
    if (!path) return '';
    const parts = path.split('/').filter(Boolean);
    if (parts.length <= 2) return path;
    return `.../${parts.slice(-2).join('/')}`;
  }

  const cardBusyState = $derived(
    runningAgent ? 'running' : queuedAgent ? 'queued' : 'idle'
  );

  // Improvement #20: agent provenance — true when this card is assigned to a persona
  const isAgentCard = $derived(card.assignee_kind === 'persona');
</script>

<!-- svelte-ignore a11y_click_events_have_key_events -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
  class="card-item"
  class:is-new={isNew && heatLevel <= 0.05}
  class:has-agent={cardBusyState !== 'idle'}
  class:rippling
  class:selected={selectedCardIds?.has(card.id) ?? false}
  style={heatLevel > 0.05
    ? `border-color:${heatBorderColor(heatLevel)};box-shadow:${heatGlow(heatLevel)}`
    : undefined}
  onclick={() => onOpen(card.id)}
  onmouseenter={onHoverStart}
  onmouseleave={onHoverEnd}
>
  <!-- Hover preview tooltip (400ms delay) -->
  {#if showPreview && (card.description || labels.length > 0)}
    <div class="card-preview" role="tooltip">
      <div class="preview-title">{card.title}</div>
      {#if previewDesc}
        <div class="preview-desc">{previewDesc}</div>
      {/if}
      {#if labels.length > 0}
        <div class="preview-labels">
          {#each labels as l}
            <span class="preview-label" style="color: {l.color}">{l.name}</span>
          {/each}
        </div>
      {/if}
    </div>
  {/if}
  <!-- Selection checkbox -->
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <input
    type="checkbox"
    class="card-select"
    checked={selectedCardIds?.has(card.id) ?? false}
    onclick={(e) => { e.stopPropagation(); }}
    onchange={(e) => {
      e.stopPropagation();
      onSelect?.(card.id, (e.target as HTMLInputElement).checked);
    }}
  />

  <!-- Top row: status icon + ID chip + repo badge + spacer + priority signal + (agent badge) -->
  <div class="card-top-row">
    <StatusIcon status={columnName} size={13} />
    <span class="card-id">{cardShortId}</span>
    {#if repoBadge}
      <span class="repo-badge">{repoBadge}</span>
    {/if}
    <span class="spacer"></span>
    {#if runningAgent}
      <div class="agent-badge" title="Agent running: {runningAgent.agent_id}">
        <span class="agent-pulse"></span>
        <span class="agent-elapsed">{agentElapsed(runningAgent.started_at)}</span>
      </div>
    {:else if queuedAgent}
      <div class="agent-badge queued" title="Agent queued: {queuedAgent.agent_id}">
        <span class="agent-pulse queued"></span>
        <span class="agent-elapsed">Queued</span>
      </div>
    {/if}
    <PrioritySignal priority={card.priority} size={13} />
    {#if isAgentCard}
      <span class="agent-chip" title="Assigned to AI agent">🤖</span>
    {/if}
    {#if card.assignee_id}
      <Avatar name={card.assignee_id} size={18} />
    {/if}
  </div>

  <!-- Title -->
  <span class="card-title">{card.title}</span>

  <!-- Label chips -->
  {#if visibleLabels.length > 0}
    <div class="label-chips">
      {#each visibleLabels as label}
        <LabelChip name={label.name} color={label.color} />
      {/each}
      {#if overflowCount > 0}
        <span class="label-overflow">+{overflowCount}</span>
      {/if}
    </div>
  {/if}

  <!-- Bottom row: date + due chip + hover start button -->
  <div class="card-footer">
    <div class="card-dates">
      <span class="created-date">{timeAgo(card.created_at)}</span>
      {#if card.due_date}
        <span
          class="due-chip"
          style="color: {urgencyColor}; border-color: {urgencyColor}40; background: {urgencyColor}12;"
        >
          {fmtDue(card.due_date)}
        </span>
      {/if}
      {#if card.estimate != null && card.estimate > 0}
        <span class="estimate-chip">{formatEstimate(card.estimate)}</span>
      {/if}
      {#if card.worktree_path}
        <span class="worktree-chip" title={card.worktree_path}>
          <span class="worktree-chip-icon" aria-hidden="true">wt</span>
          <span>{formatWorktree(card.worktree_path)}</span>
        </span>
      {/if}
    </div>
    {#if runningAgent}
      <div class="agent-run-badge">
        <span class="arb-dot" aria-hidden="true"></span>
        <span class="arb-label">Running {agentElapsed(runningAgent.started_at)}</span>
      </div>
    {:else if queuedAgent}
      <div class="agent-run-badge queued">
        <span class="arb-dot queued" aria-hidden="true"></span>
        <span class="arb-label queued">Queued</span>
      </div>
    {/if}
    {#if !runningAgent && !queuedAgent}
      <button
        class="start-btn hover-visible"
        onclick={startWork}
        disabled={starting}
        title="Spawn an agent to work on this card (Claude Code)"
      >
        {starting ? '…' : '▶ Start'}
      </button>
    {/if}
    <span
      class="time-in-status"
      style="color: {timeInStatusColor(card.updated_at)}"
      title="Time in this column"
    >
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
        <circle cx="5" cy="5" r="4" stroke="currentColor" stroke-width="1"/>
        <line x1="5" y1="2.5" x2="5" y2="5" stroke="currentColor" stroke-width="1" stroke-linecap="round"/>
        <line x1="5" y1="5" x2="7" y2="6.5" stroke="currentColor" stroke-width="1" stroke-linecap="round"/>
      </svg>
      {timeInStatus(card.updated_at)}
    </span>
  </div>

  <div class="particles" class:active={decaying}>
    <span class="particle"></span>
    <span class="particle"></span>
    <span class="particle"></span>
    <span class="particle"></span>
    <span class="particle"></span>
  </div>
</div>

<style>
  .card-item {
    position: relative;
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.07);
    border-radius: 7px;
    padding: var(--card-padding, 0.6rem 0.75rem);
    cursor: pointer;
    /* Only background-color animates on hover. Box-shadow, border-color,
       and transform all change instantly. Cheap element-local paint. */
    transition: background-color 100ms ease;
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    /* Isolation hint: tell the browser this card's layout, style, and
       paint are self-contained. Browser can skip hover recomputation
       on neighbors and skip repainting outside this box. Massive hover
       perf win when you have 20+ cards visible. */
    contain: layout style paint;
  }
  .card-item:hover {
    background: rgba(255,255,255,0.055);
    border-color: rgba(255,255,255,0.14);
  }
  .card-item.selected {
    border-color: oklch(0.72 0.28 310 / 0.45);
    background: oklch(0.72 0.28 310 / 0.06);
    box-shadow:
      0 0 0 1px oklch(0.72 0.28 310 / 0.20),
      0 0 16px oklch(0.72 0.28 310 / 0.18);
  }

  /* Selection checkbox */
  .card-select {
    position: absolute;
    top: 0.5rem;
    left: 0.5rem;
    opacity: 0;
    transition: opacity 150ms ease;
    cursor: pointer;
    accent-color: oklch(0.72 0.28 310);
    width: 14px;
    height: 14px;
    z-index: 2;
  }
  .card-item:hover .card-select,
  .card-item.selected .card-select {
    opacity: 1;
  }
  .card-item.is-new {
    box-shadow:
      0 0 18px oklch(0.78 0.16 85 / 0.22),
      inset 0 0 8px oklch(0.78 0.16 85 / 0.05);
    border-color: oklch(0.78 0.16 85 / 0.26);
  }
  .card-item.has-agent {
    border-color: oklch(0.55 0.20 265 / 0.30);
    animation: agent-pulse var(--pulse-rate, 10s) ease-in-out infinite;
  }
  @keyframes agent-pulse {
    0%, 100% { box-shadow: 0 0  8px oklch(0.55 0.20 265 / 0.12); }
    50%       { box-shadow: 0 0 20px oklch(0.58 0.22 265 / 0.32); }
  }

  /* Top row — Linear-style density: status icon + ID chip + signal + agent */
  .card-top-row {
    display: flex;
    align-items: center;
    gap: 0.4rem;
  }
  .card-id {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 0.62rem;
    color: oklch(0.78 0.16 85 / 0.9);
    background: oklch(0.78 0.16 85 / 0.08);
    border: 1px solid oklch(0.78 0.16 85 / 0.22);
    padding: 0.05rem 0.3rem;
    border-radius: 3px;
    letter-spacing: 0.02em;
    line-height: 1.3;
  }
  .repo-badge {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 0.60rem;
    color: oklch(0.68 0.18 265 / 0.95);
    background: oklch(0.68 0.18 265 / 0.10);
    border: 1px solid oklch(0.68 0.18 265 / 0.28);
    padding: 0.05rem 0.3rem;
    border-radius: 3px;
    letter-spacing: 0.02em;
    line-height: 1.3;
    flex-shrink: 0;
  }
  .spacer { flex: 1; }

  /* Improvement #20: agent provenance chip */
  .agent-chip {
    font-size: 0.65rem;
    background: oklch(0.72 0.28 310 / 0.15);
    border: 1px solid oklch(0.72 0.28 310 / 0.25);
    border-radius: 4px;
    padding: 0.05rem 0.3rem;
    flex-shrink: 0;
  }

  /* Title */
  .card-title {
    font-size: 0.82rem;
    color: #c8ccd8;
    line-height: 1.4;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  /* Label chips — wrapper only; the chip styling lives in LabelChip.svelte */
  .label-chips {
    display: flex;
    align-items: center;
    gap: 0.3rem;
    flex-wrap: wrap;
  }
  .label-overflow {
    font-size: 0.6rem;
    color: oklch(0.55 0.04 310 / 0.4);
    font-family: 'JetBrains Mono', monospace;
  }

  /* Footer row */
  .card-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 0.05rem;
  }
  .card-dates {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    flex-wrap: wrap;
  }
  .created-date {
    font-size: 0.62rem;
    color: rgba(255,255,255,0.28);
    font-family: 'JetBrains Mono', monospace;
    letter-spacing: 0.02em;
  }
  /* Due-date chip. Color/border/bg come from inline urgency styles;
     base shape is here. */
  .due-chip {
    font-size: 0.62rem;
    border: 1px solid;
    border-radius: 3px;
    padding: 0.08rem 0.35rem;
    font-family: 'JetBrains Mono', monospace;
    letter-spacing: 0.02em;
  }
  .estimate-chip,
  .worktree-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.62rem;
    border-radius: 3px;
    padding: 0.08rem 0.35rem;
    font-family: 'JetBrains Mono', monospace;
    letter-spacing: 0.02em;
  }
  .estimate-chip {
    color: oklch(0.80 0.15 85 / 0.90);
    background: oklch(0.80 0.15 85 / 0.10);
    border: 1px solid oklch(0.80 0.15 85 / 0.26);
  }
  .worktree-chip {
    color: oklch(0.74 0.13 210 / 0.90);
    background: oklch(0.74 0.13 210 / 0.10);
    border: 1px solid oklch(0.74 0.13 210 / 0.26);
    max-width: 132px;
  }
  .worktree-chip span:last-child {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .worktree-chip-icon {
    flex-shrink: 0;
    font-size: 0.58rem;
    text-transform: uppercase;
  }

  /* Agent running indicator — compact ticker in the card header */
  .agent-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    padding: 0.08rem 0.35rem;
    border-radius: 999px;
    background: oklch(0.72 0.19 145 / 0.10);
    border: 1px solid oklch(0.72 0.19 145 / 0.24);
  }
  .agent-badge.queued {
    background: oklch(0.72 0.28 310 / 0.10);
    border-color: oklch(0.72 0.28 310 / 0.24);
  }
  .agent-pulse {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: oklch(0.72 0.19 145);
    box-shadow: 0 0 6px oklch(0.72 0.19 145 / 0.60);
    animation: dot-pulse 1.5s ease-in-out infinite;
  }
  .agent-pulse.queued {
    background: oklch(0.72 0.28 310);
    box-shadow: 0 0 6px oklch(0.72 0.28 310 / 0.60);
  }
  .agent-elapsed {
    font-size: 0.58rem;
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    color: oklch(0.80 0.12 145 / 0.90);
    white-space: nowrap;
  }
  @keyframes dot-pulse {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.4; }
  }

  .agent-run-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    padding: 0.18rem 0.5rem;
    background: oklch(0.72 0.19 145 / 0.12);
    border: 1px solid oklch(0.72 0.19 145 / 0.30);
    border-radius: 4px;
    margin-top: 0.35rem;
  }
  .agent-run-badge.queued {
    background: oklch(0.72 0.28 310 / 0.12);
    border-color: oklch(0.72 0.28 310 / 0.30);
  }
  .arb-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: oklch(0.72 0.19 145);
    box-shadow: 0 0 6px oklch(0.72 0.19 145 / 0.60);
    animation: arb-pulse 1.5s ease-in-out infinite;
    flex-shrink: 0;
  }
  .arb-dot.queued {
    background: oklch(0.72 0.28 310);
    box-shadow: 0 0 6px oklch(0.72 0.28 310 / 0.60);
  }
  @keyframes arb-pulse {
    0%, 100% { opacity: 1; box-shadow: 0 0 6px oklch(0.72 0.19 145 / 0.60); }
    50% { opacity: 0.5; box-shadow: 0 0 2px oklch(0.72 0.19 145 / 0.20); }
  }
  .arb-label {
    font-size: 0.65rem;
    font-family: 'JetBrains Mono', monospace;
    color: oklch(0.72 0.19 145 / 0.90);
    white-space: nowrap;
  }
  .arb-label.queued {
    color: oklch(0.78 0.22 310 / 0.92);
  }

  /* Start button — hidden by default, shown on card hover */
  .start-btn {
    background: oklch(0.72 0.28 310 / 0.06);
    border: 1px solid oklch(0.72 0.28 310 / 0.18);
    color: oklch(0.80 0.26 310 / 0.74);
    padding: 0.2rem 0.55rem;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.7rem;
    transition: background-color 100ms ease, color 100ms ease, border-color 100ms ease;
    opacity: 0;
    pointer-events: none;
    position: relative;
  }
  .card-item:hover .start-btn {
    opacity: 1;
    pointer-events: auto;
  }
  .start-btn:hover:not(:disabled) {
    background: oklch(0.72 0.28 310 / 0.14);
    border-color: oklch(0.74 0.28 310 / 0.46);
    color: oklch(0.86 0.25 310 / 0.92);
    box-shadow: 0 0 10px oklch(0.72 0.28 310 / 0.18), 0 0 0 2px oklch(0.72 0.28 310 / 0.07);
  }
  .start-btn:disabled { opacity: 0.4; cursor: not-allowed; }

  /* Time-in-status indicator */
  .time-in-status {
    display: flex;
    align-items: center;
    gap: 2px;
    font-size: 0.6rem;
    font-family: 'JetBrains Mono', monospace;
    margin-left: auto;
    flex-shrink: 0;
  }

  /* ── Hover preview tooltip (refinement #18) ──────── */
  .card-preview {
    position: absolute;
    z-index: 30;
    left: calc(100% + 8px);
    top: 0;
    width: 260px;
    max-height: 200px;
    background: oklch(0.11 0.02 275);
    border: 1px solid oklch(0.35 0.06 275 / 0.50);
    border-radius: 6px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.40);
    padding: 0.65rem 0.75rem;
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    pointer-events: none;
    overflow: hidden;
  }
  .preview-title {
    font-size: 0.78rem;
    font-weight: 600;
    color: oklch(0.94 0.08 310);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .preview-desc {
    font-size: 0.7rem;
    color: oklch(0.68 0.05 310 / 0.75);
    line-height: 1.5;
    white-space: pre-wrap;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    -webkit-box-orient: vertical;
  }
  .preview-labels {
    display: flex;
    gap: 0.3rem;
    flex-wrap: wrap;
  }
  .preview-label {
    font-size: 0.62rem;
    font-weight: 500;
  }

  /* ── Signal ripple ───────────────────────────────── */
  .card-item.rippling {
    animation: signal-ripple 0.6s ease-out forwards;
  }
  @keyframes signal-ripple {
    0%   { box-shadow: 0 0 0 0 oklch(0.78 0.26 310 / 0.58); }
    40%  { box-shadow: 0 0 0 8px oklch(0.72 0.28 310 / 0.14), inset 0 0 12px oklch(0.72 0.28 310 / 0.06); }
    100% { box-shadow: 0 0 0 16px oklch(0.72 0.28 310 / 0); }
  }

  /* ── Particle decay ──────────────────────────────── */
  .particles {
    position: absolute;
    inset: 0;
    pointer-events: none;
    overflow: visible;
  }
  .particle {
    position: absolute;
    left: 50%;
    bottom: 50%;
    width: 3px;
    height: 3px;
    border-radius: 50%;
    background: oklch(0.80 0.26 310);
    opacity: 0;
  }
  .particles.active .particle {
    animation: particle-rise 1.2s ease-out forwards;
  }
  .particles.active .particle:nth-child(1) { animation-delay: 0ms;   --drift: -18px; }
  .particles.active .particle:nth-child(2) { animation-delay: 80ms;  --drift: -8px; }
  .particles.active .particle:nth-child(3) { animation-delay: 160ms; --drift: 0px; }
  .particles.active .particle:nth-child(4) { animation-delay: 240ms; --drift: 8px; }
  .particles.active .particle:nth-child(5) { animation-delay: 320ms; --drift: 18px; }
  @keyframes particle-rise {
    0%   { opacity: 0.9; transform: translate(var(--drift, 0px), 0); }
    100% { opacity: 0;   transform: translate(var(--drift, 0px), -40px); }
  }
</style>
